import i18n from 'i18next';

import {
    ANONYMOUS_NODE_NAME,
    CommentInfo,
    CommentText,
    DraftInfo,
    DraftType,
    MediaAttachment,
    Node,
    NodeApiError,
    ReactionAttributes,
    ReactionInfo,
    RepliedTo
} from "api";
import { updateMediaCaptions } from "state/mediaupload/media-upload";
import { Storage } from "storage";
import { WithContext } from "state/action-types";
import { saga } from "state/saga";
import { barrier, dispatch, select } from "state/store-sagas";
import { homeIntroduced } from "state/init-barriers";
import { errorThrown } from "state/error/actions";
import { ClientAction } from "state/action";
import {
    AttachmentCopyLinkAction,
    closeCommentDialog,
    CommentComposeCancelAction,
    commentComposeCancelled,
    CommentCopyLinkAction,
    CommentDeleteAction,
    commentDeleted,
    commentDeleteFailed,
    CommentDialogCommentLoadAction,
    commentDialogCommentLoaded,
    commentDialogCommentLoadFailed,
    CommentDialogCommentResetAction,
    commentDraftAbsent,
    commentDraftCompleteLoading,
    CommentDraftCompleteLoadingAction,
    commentDraftDelete,
    CommentDraftDeleteAction,
    commentDraftDeleted,
    CommentDraftLoadAction,
    commentDraftLoaded,
    commentDraftLoadFailed,
    CommentDraftSaveAction,
    commentDraftSaved,
    CommentDraftSavedAction,
    commentDraftSaveFailed,
    commentLoad,
    CommentLoadAction,
    commentLoadFailed,
    CommentPostAction,
    commentPosted,
    commentPostFailed,
    CommentReactAction,
    CommentReactionDeleteAction,
    CommentReactionLoadAction,
    commentReactionSet,
    commentRepliedToSet,
    commentRepliedToUnset,
    CommentReplyAction,
    CommentsBlockedUsersLoadAction,
    commentsBlockedUsersLoaded,
    commentsBlockedUsersLoadFailed,
    commentSet,
    CommentSetVisibilityAction,
    CommentsFutureSliceLoadAction,
    commentsFutureSliceLoadFailed,
    commentsFutureSliceSet,
    CommentsLoadAllAction,
    CommentsPastSliceLoadAction,
    commentsPastSliceLoadFailed,
    commentsPastSliceSet,
    CommentsReceiverFeaturesLoadAction,
    commentsReceiverFeaturesLoaded,
    CommentsReceiverSwitchAction,
    commentsReceiverSwitched,
    commentsScrollToComposer,
    commentsSliceUpdate,
    CommentsUpdateAction,
    CommentVerifyAction,
    commentVerifyFailed,
    DetailedPostingLoadAction,
    DetailedPostingLoadAttachedAction,
    detailedPostingLoaded,
    detailedPostingLoadedAttached,
    detailedPostingLoadFailed,
    FocusedCommentLoadAction,
    focusedCommentLoaded,
    focusedCommentLoadFailed,
    GlanceCommentLoadAction,
    glanceCommentLoaded,
    glanceCommentLoadFailed
} from "state/detailedposting/actions";
import {
    getComment,
    getCommentComposerRepliedToName,
    getCommentDialogCommentId,
    getCommentsReceiverName,
    getCommentsReceiverPostingId,
    getCommentsState,
    getDetailedPosting,
    getDetailedPostingId,
    isCommentComposerReplied
} from "state/detailedposting/selectors";
import { fillActivityReaction, fillActivityReactionsInPostings } from "state/activityreactions/sagas";
import { fillBlockedOperations, fillBlockedOperationsInPostings } from "state/blockedoperations/sagas";
import { postingCommentCountUpdate, postingCommentsSet, postingsSet } from "state/postings/actions";
import { getPosting, isPostingCached } from "state/postings/selectors";
import { postingGetLink } from "state/postings/sagas";
import { getOwnerFullName, getOwnerName, getOwnerSourceUri, isPermitted, isPrincipalIn } from "state/node/selectors";
import { anonymousFullNameSet } from "state/home/actions";
import { isConnectedToHome } from "state/home/selectors";
import { flashBox } from "state/flashbox/actions";
import { fillSubscription } from "state/subscriptions/sagas";
import { confirmBox } from "state/confirmbox/actions";
import { getNodeUri } from "state/naming/sagas";
import {
    loadRemoteMediaInDraftAttachments,
    loadRemoteMediaInEntries,
    loadRemoteMediaInEntry
} from "state/remotemedia/sagas";
import { deleteDraft } from "state/drafts/sagas";
import * as Browser from "ui/browser";
import { uiEventCommentQuote } from "ui/ui-events";
import { toAvatarDescription } from "util/avatar";
import { getWindowSelectionHtml } from "util/ui";
import { absoluteNodeName, REL_CURRENT, REL_HOME } from "util/rel-node-name";
import { clipboardCopy } from "util/clipboard";
import { delay, notNull } from "util/misc";
import { urlWithParameters, ut } from "util/url";
import { universalLocation } from "util/universal-url";

export default [
    saga("DETAILED_POSTING_LOAD", "", detailedPostingLoadSaga),
    saga("DETAILED_POSTING_LOAD_ATTACHED", "", detailedPostingLoadAttachedSaga),
    saga("COMMENTS_RECEIVER_SWITCH", "", commentsReceiverSwitchSaga),
    saga("COMMENTS_RECEIVER_FEATURES_LOAD", "", commentsReceiverFeaturesLoadSaga),
    saga("COMMENTS_LOAD_ALL", "", commentsLoadAllSaga),
    saga("COMMENTS_PAST_SLICE_LOAD", "", commentsPastSliceLoadSaga),
    saga("COMMENTS_FUTURE_SLICE_LOAD", "", commentsFutureSliceLoadSaga),
    saga("COMMENTS_UPDATE", "", commentsUpdateSaga),
    saga("COMMENTS_BLOCKED_USERS_LOAD", "", commentsBlockedUsersLoadSaga),
    saga("COMMENT_LOAD", payload => payload.commentId, commentLoadSaga),
    saga("COMMENT_POST", null, commentPostSaga),
    saga("COMMENT_DRAFT_LOAD", "", commentDraftLoadSaga),
    saga("COMMENT_DRAFT_COMPLETE_LOADING", "", commentDraftCompleteLoadingSaga),
    saga("COMMENT_DRAFT_SAVE", null, commentDraftSaveSaga),
    saga("COMMENT_DRAFT_DELETE", "", commentDraftDeleteSaga),
    saga("COMMENT_COMPOSE_CANCEL", "", commentComposeCancelSaga),
    saga("COMMENT_DELETE", payload => payload.commentId, commentDeleteSaga),
    saga("COMMENT_SET_VISIBILITY", payload => payload.commentId, commentSetVisibilitySaga),
    saga("FOCUSED_COMMENT_LOAD", "", focusedCommentLoadSaga),
    saga("COMMENT_COPY_LINK", null, commentCopyLinkSaga),
    saga("COMMENT_DIALOG_COMMENT_LOAD", "", commentDialogCommentLoadSaga),
    saga("COMMENT_DIALOG_COMMENT_RESET", "", commentDialogCommentResetSaga),
    saga("COMMENT_VERIFY", payload => payload.commentId, commentVerifySaga),
    saga("COMMENT_REACT", null, commentReactSaga),
    saga("COMMENT_REACTION_LOAD", payload => `${payload.id}:${payload.postingId}`, commentReactionLoadSaga),
    saga("COMMENT_REACTION_DELETE", payload => `${payload.id}:${payload.postingId}`, commentReactionDeleteSaga),
    saga("COMMENT_REPLY", "", commentReplySaga),
    saga("GLANCE_COMMENT_LOAD", null, glanceCommentLoadSaga),
    saga("ATTACHMENT_COPY_LINK", null, attachmentCopyLinkSaga),
];

async function detailedPostingLoadSaga(action: WithContext<DetailedPostingLoadAction>): Promise<void> {
    await homeIntroduced();
    const id = select(getDetailedPostingId);
    if (id == null) {
        dispatch(detailedPostingLoadFailed().causedBy(action));
        return;
    }

    try {
        const posting = await Node.getPosting(action, REL_CURRENT, id, false, ["posting.not-found"]);
        await fillActivityReaction(action, posting);
        await fillBlockedOperations(action, posting);
        dispatch(detailedPostingLoaded(posting).causedBy(action));
        loadRemoteMediaInEntry(action, posting);
        await fillSubscription(action, posting);
    } catch (e) {
        dispatch(detailedPostingLoadFailed().causedBy(action));
        dispatch(errorThrown(e));
    }
}

async function detailedPostingLoadAttachedSaga(action: WithContext<DetailedPostingLoadAttachedAction>): Promise<void> {
    await homeIntroduced();
    const id = select(getDetailedPostingId);
    if (id == null) {
        return;
    }
    const loaded = select(state => {
        const media = getPosting(state, id, REL_CURRENT)?.media;
        if (media == null) {
            return true;
        }
        return media
            .map(ma => ma.postingId)
            .filter(notNull)
            .every(p => isPostingCached(state, p, REL_CURRENT));
    });
    if (loaded) {
        dispatch(detailedPostingLoadedAttached().causedBy(action));
        return;
    }

    try {
        const postings = await Node.getPostingsAttachedToPosting(action, REL_CURRENT, id);
        await fillActivityReactionsInPostings(action, postings);
        await fillBlockedOperationsInPostings(action, postings);
        dispatch(postingsSet(postings, REL_CURRENT).causedBy(action));
        dispatch(detailedPostingLoadedAttached().causedBy(action));
    } catch (e) {
        dispatch(errorThrown(e));
    }
}

async function commentsReceiverSwitchSaga(action: CommentsReceiverSwitchAction): Promise<void> {
    await homeIntroduced();
    const {ownerName, ownerFullName, ownerSourceUri, posting} = select(state => ({
        ownerName: getOwnerName(state),
        ownerFullName: getOwnerFullName(state),
        ownerSourceUri: getOwnerSourceUri(state),
        posting: getDetailedPosting(state)
    }));
    if (posting == null || ownerName == null) {
        return;
    }
    const receiverName = posting.receiverName ?? ownerName;
    const receiverFullName = posting.receiverFullName ?? ownerFullName;
    const receiverSourceUri = posting.receiverName != null ? posting.receiverSourceUri ?? null : ownerSourceUri;
    const receiverPostingId = posting.receiverPostingId ?? posting.id;
    dispatch(
        commentsReceiverSwitched(receiverName, receiverFullName, receiverSourceUri, receiverPostingId).causedBy(action)
    );
}

async function commentsReceiverFeaturesLoadSaga(
    action: WithContext<CommentsReceiverFeaturesLoadAction>
): Promise<void> {
    await homeIntroduced();
    const nodeName = select(getCommentsReceiverName);
    if (nodeName == null) {
        return;
    }
    try {
        const features = await Node.getFeatures(action, nodeName);
        dispatch(commentsReceiverFeaturesLoaded(nodeName, features).causedBy(action));
    } catch (e) {
        dispatch(errorThrown(e));
    }
}

async function commentsLoadAllSaga(action: WithContext<CommentsLoadAllAction>): Promise<void> {
    await homeIntroduced();
    let {receiverName, receiverPostingId, before, after} = select(getCommentsState);
    if (receiverName == null || receiverPostingId == null) {
        return;
    }
    try {
        while (after > Number.MIN_SAFE_INTEGER) {
            const slice = await Node.getCommentsSlice(action, receiverName, receiverPostingId, null, after, 20);
            dispatch(commentsPastSliceSet(
                receiverName, receiverPostingId, slice.comments, slice.before, slice.after, slice.total,
                slice.totalInPast, slice.totalInFuture, null
            ).causedBy(action));
            loadRemoteMediaInEntries(action, slice.comments);
            after = slice.after;
        }
        while (before < Number.MAX_SAFE_INTEGER) {
            const slice = await Node.getCommentsSlice(action, receiverName, receiverPostingId, before, null, 20);
            dispatch(commentsFutureSliceSet(
                receiverName, receiverPostingId, slice.comments, slice.before, slice.after, slice.total,
                slice.totalInPast, slice.totalInFuture, null
            ).causedBy(action));
            loadRemoteMediaInEntries(action, slice.comments);
            before = slice.before;
        }
    } catch (e) {
        dispatch(errorThrown(e));
    }
}

async function commentsPastSliceLoadSaga(action: WithContext<CommentsPastSliceLoadAction>): Promise<void> {
    await homeIntroduced();
    const {receiverName, receiverPostingId, after} = select(getCommentsState);
    if (receiverName == null || receiverPostingId == null) {
        dispatch(commentsPastSliceLoadFailed(receiverName, receiverPostingId).causedBy(action));
        return;
    }
    try {
        const slice = await Node.getCommentsSlice(action, receiverName, receiverPostingId, null, after, 20);
        dispatch(commentsPastSliceSet(
            receiverName, receiverPostingId, slice.comments, slice.before, slice.after, slice.total,
            slice.totalInPast, slice.totalInFuture, action.payload.anchor
        ).causedBy(action));
        loadRemoteMediaInEntries(action, slice.comments);
    } catch (e) {
        dispatch(commentsPastSliceLoadFailed(receiverName, receiverPostingId).causedBy(action));
        dispatch(errorThrown(e));
    }
}

async function commentsFutureSliceLoadSaga(action: WithContext<CommentsFutureSliceLoadAction>): Promise<void> {
    await homeIntroduced();
    const {receiverName, receiverPostingId, before} = select(getCommentsState);
    if (receiverName == null || receiverPostingId == null) {
        dispatch(commentsFutureSliceLoadFailed(receiverName, receiverPostingId).causedBy(action));
        return;
    }
    try {
        const slice = await Node.getCommentsSlice(action, receiverName, receiverPostingId, before, null, 20);
        dispatch(commentsFutureSliceSet(
            receiverName, receiverPostingId, slice.comments, slice.before, slice.after, slice.total, slice.totalInPast,
            slice.totalInFuture, action.payload.anchor
        ).causedBy(action));
        loadRemoteMediaInEntries(action, slice.comments);
    } catch (e) {
        dispatch(commentsFutureSliceLoadFailed(receiverName, receiverPostingId).causedBy(action));
        dispatch(errorThrown(e));
    }
}

async function commentsUpdateSaga(action: WithContext<CommentsUpdateAction>): Promise<void> {
    await homeIntroduced();
    let {receiverName, receiverPostingId, before, after} = select(getCommentsState);
    if (receiverName == null || receiverPostingId == null) {
        return;
    }
    try {
        while (before > after) {
            const slice = await Node.getCommentsSlice(action, receiverName, receiverPostingId, after, null, 20);
            dispatch(commentsSliceUpdate(
                receiverName, receiverPostingId, slice.comments, slice.before, slice.after, slice.total,
                slice.totalInPast, slice.totalInFuture
            ).causedBy(action));
            loadRemoteMediaInEntries(action, slice.comments);
            if (after === slice.before) {
                break;
            }
            after = slice.before;
        }
    } catch (e) {
        dispatch(errorThrown(e));
    }
}

async function commentsBlockedUsersLoadSaga(action: WithContext<CommentsBlockedUsersLoadAction>): Promise<void> {
    await homeIntroduced();
    const {homeOwnerName} = action.context;

    const {receiverName, receiverPostingId} = select(state => ({
        receiverName: getCommentsReceiverName(state),
        receiverPostingId: getCommentsReceiverPostingId(state)
    }));
    if (receiverName == null || receiverPostingId == null || homeOwnerName == null) {
        dispatch(commentsBlockedUsersLoadFailed().causedBy(action));
        return;
    }

    const entryId = receiverName === homeOwnerName ? receiverPostingId : null;
    const entryNodeName = receiverName === homeOwnerName ? null : receiverName;
    const entryPostingId = receiverName === homeOwnerName ? null : receiverPostingId;

    try {
        const blocked = await Node.searchBlockedUsers(action, REL_HOME, {
            entryId,
            entryNodeName,
            entryPostingId,
            strict: true
        });
        dispatch(commentsBlockedUsersLoaded(receiverName, receiverPostingId, blocked).causedBy(action));
    } catch (e) {
        dispatch(commentsBlockedUsersLoadFailed().causedBy(action));
        dispatch(errorThrown(e));
    }
}

async function commentLoadSaga(action: WithContext<CommentLoadAction>): Promise<void> {
    await homeIntroduced();
    const {commentId} = action.payload;

    const {receiverName, receiverPostingId} = select(getCommentsState);
    if (receiverName == null || receiverPostingId == null) {
        return;
    }
    try {
        const comment = await Node.getComment(
            action, receiverName, receiverPostingId, commentId, false, ["comment.not-found"]
        );
        dispatch(commentSet(receiverName, comment).causedBy(action));
        loadRemoteMediaInEntry(action, comment);
    } catch (e) {
        dispatch(commentLoadFailed(receiverName, receiverPostingId, commentId).causedBy(action));
        dispatch(errorThrown(e));
    }
}

async function commentPostSaga(action: WithContext<CommentPostAction>): Promise<void> {
    const {commentId, postingId, commentText, commentSourceText, captions, compressionPending, formId} = action.payload;

    if (commentText.ownerName === ANONYMOUS_NODE_NAME) {
        const fullName = commentText.ownerFullName?.trim() || null;
        Storage.storeAnonymousFullName(fullName);
        dispatch(anonymousFullNameSet(fullName));
    }

    const {receiverName, receiverPostingId} = select(getCommentsState);
    if (receiverName == null || receiverPostingId == null) {
        return;
    }

    const draftId = select(state =>
        (commentId == null ? state.detailedPosting.compose.draft : state.detailedPosting.commentDialog.draft)?.id
    ) ?? null;

    try {
        if (!compressionPending) {
            let comment: CommentInfo;
            if (commentId == null) {
                dispatch(postingCommentCountUpdate(receiverPostingId, receiverName, 1).causedBy(action));
                const created = await Node.createComment(action, receiverName, receiverPostingId, commentText);
                dispatch(postingCommentsSet(postingId, created.total, REL_CURRENT).causedBy(action));
                comment = created.comment;
                if (comment.premoderating) {
                    dispatch(flashBox(i18n.t("your-comment-published-once-approved")).causedBy(action));
                }
            } else {
                comment = await Node.updateComment(action, receiverName, receiverPostingId, commentId, commentText);
            }
            await updateMediaCaptions(action, receiverName, comment.media, captions);
            dispatch(commentSet(receiverName, comment).causedBy(action));
            loadRemoteMediaInEntry(action, comment);

            dispatch(
                commentPosted(receiverName, receiverPostingId, comment.id, comment.moment, formId).causedBy(action)
            );

            if (receiverName !== commentText.ownerName && commentText.ownerName !== ANONYMOUS_NODE_NAME) {
                await Node.updateRemoteComment(
                    action, REL_HOME, receiverName, receiverPostingId, comment.id, commentSourceText
                );
            }

            if (comment.signature == null) {
                refreshComment(receiverName, receiverPostingId, comment.id);
            }
        } else {
            if (commentId == null) {
                await Node.createRemoteComment(action, REL_HOME, receiverName, receiverPostingId, commentSourceText);
            } else {
                await Node.updateRemoteComment(
                    action, REL_HOME, receiverName, receiverPostingId, commentId, commentSourceText
                );
            }
            dispatch(
                commentPosted(receiverName, receiverPostingId, commentId, null, formId).causedBy(action)
            );
            dispatch(flashBox(i18n.t("video-comment-being-processed")).causedBy(action));
        }

        if (draftId != null) {
            await Node.deleteDraft(action, REL_HOME, draftId, ["draft.not-found"]);
        } else {
            deleteObsoleteDraft(receiverName, receiverPostingId, commentId, formId);
        }
    } catch (e) {
        dispatch(commentPostFailed(receiverName, receiverPostingId).causedBy(action));
        dispatch(errorThrown(e));
    }
}

async function deleteObsoleteDraft(
    nodeName: string, postingId: string, commentId: string | null, formId: number | null
): Promise<void> {
    const action = await barrier<CommentDraftSavedAction>("COMMENT_DRAFT_SAVED", true, 5000);
    if (
        action == null
        || action.payload.nodeName !== nodeName
        || action.payload.postingId !== postingId
        || action.payload.commentId !== commentId
        || action.payload.formId !== formId
    ) {
        return;
    }
    await deleteDraft(action, action.payload.draft.id);
}

async function refreshComment(commentReceiverName: string, commentPostingId: string, commentId: string): Promise<void> {
    await delay(3000);

    const {receiverName, receiverPostingId} = select(getCommentsState);
    if (commentReceiverName !== receiverName || commentPostingId !== receiverPostingId) {
        return;
    }

    const comment = select(state => getComment(state, commentId));
    if (comment != null && comment.signature == null) {
        dispatch(commentLoad(commentId));
    }
}

async function loadRemoteMediaAttachments( // FIXME obsolete
    action: WithContext<ClientAction>, attachments: MediaAttachment[] | null
): Promise<void> {
    if (attachments == null) {
        return;
    }

    for (const attachment of attachments) {
        if (attachment.media == null && attachment.remoteMedia != null) {
            attachment.media = await Node.getPrivateMediaInfo(
                action, attachment.remoteMedia.nodeName, attachment.remoteMedia.mediaId
            );
        }
    }
}

async function loadRepliedTo(
    action: WithContext<ClientAction>, nodeName: string, postingId: string, id: string
): Promise<RepliedTo | null> {
    let repliedToComment: CommentInfo | null = select(state => getComment(state, id));
    if (repliedToComment == null) {
        repliedToComment = await Node.getComment(action, nodeName, postingId, id, false, ["comment.not-found"]);
    }
    if (repliedToComment != null && repliedToComment.digest != null) {
        return {
            id: repliedToComment.id,
            name: repliedToComment.ownerName,
            fullName: repliedToComment.ownerFullName,
            sourceUri: repliedToComment.ownerSourceUri,
            avatar: repliedToComment.ownerAvatar,
            heading: repliedToComment.heading,
            digest: repliedToComment.digest
        }
    } else {
        return null;
    }
}

async function commentDraftLoadSaga(action: WithContext<CommentDraftLoadAction>): Promise<void> {
    const {isDialog} = action.payload;

    const {connectedToHome, nodeName, postingId, commentId} = select(state => ({
        connectedToHome: isConnectedToHome(state),
        nodeName: getCommentsState(state).receiverName,
        postingId: getCommentsState(state).receiverPostingId,
        commentId: isDialog ? state.detailedPosting.commentDialog.commentId : null
    }));

    if (nodeName == null || postingId == null) {
        return;
    }

    if (!connectedToHome) {
        dispatch(commentDraftAbsent(nodeName, postingId, commentId).causedBy(action));
        return;
    }

    try {
        const draftType: DraftType = commentId != null ? "comment-update" : "new-comment";
        const drafts = await Node.getDrafts(action, REL_HOME, draftType, nodeName, postingId, commentId)
        const draft = drafts.length > 0 ? drafts[0] : null;
        if (draft != null) {
            if (commentId != null) {
                dispatch(confirmBox({
                    message: i18n.t("comment-has-draft-want-load"),
                    onYes: commentDraftCompleteLoading(isDialog, draft).causedBy(action),
                    onNo: [
                        commentDraftDelete(draft).causedBy(action),
                        commentDraftAbsent(nodeName, postingId, commentId).causedBy(action),
                    ],
                    variant: "primary"
                }).causedBy(action));
            } else {
                dispatch(commentDraftCompleteLoading(isDialog, draft).causedBy(action));
                loadRemoteMediaInDraftAttachments(action, draft.media);
            }
        } else {
            dispatch(commentDraftAbsent(nodeName, postingId, commentId).causedBy(action));
        }
    } catch (e) {
        dispatch(commentDraftLoadFailed(nodeName, postingId, commentId).causedBy(action));
        dispatch(errorThrown(e));
    }
}

async function commentDraftCompleteLoadingSaga(action: WithContext<CommentDraftCompleteLoadingAction>): Promise<void> {
    const {isDialog, draft} = action.payload;

    const {nodeName, postingId, commentId} = select(state => ({
        nodeName: getCommentsState(state).receiverName,
        postingId: getCommentsState(state).receiverPostingId,
        commentId: isDialog ? state.detailedPosting.commentDialog.commentId : null
    }));

    if (nodeName == null || postingId == null) {
        return;
    }

    try {
        await loadRemoteMediaAttachments(action, draft.media ?? null);
        dispatch(commentDraftLoaded(draft).causedBy(action));

        let repliedTo: RepliedTo | null = null;
        if (commentId == null && draft.repliedToId != null) {
            repliedTo = await loadRepliedTo(action, nodeName, postingId, draft.repliedToId);
        }
        if (repliedTo != null) {
            dispatch(commentRepliedToSet(
                repliedTo.id, repliedTo.name, repliedTo.fullName ?? null, repliedTo.sourceUri ?? null,
                repliedTo.heading ?? ""
            ).causedBy(action));
        } else {
            dispatch(commentRepliedToUnset().causedBy(action));
        }
    } catch (e) {
        dispatch(commentDraftLoadFailed(nodeName, postingId, commentId).causedBy(action));
        dispatch(errorThrown(e));
    }
}

async function commentDraftSaveSaga(action: WithContext<CommentDraftSaveAction>): Promise<void> {
    const {draftId, draftText, formId} = action.payload;

    if (draftText.receiverPostingId == null) {
        return;
    }

    try {
        let draft: DraftInfo;
        if (draftId == null) {
            draft = await Node.createDraft(action, REL_HOME, draftText);
        } else {
            draft = await Node.updateDraft(action, REL_HOME, draftId, draftText, ["draft.not-found"]);
        }
        dispatch(commentDraftSaved(
            draftText.receiverName, draftText.receiverPostingId, draftText.receiverCommentId ?? null, draft, formId
        ).causedBy(action));
    } catch (e) {
        dispatch(commentDraftSaveFailed(
            draftText.receiverName, draftText.receiverPostingId, draftText.receiverCommentId ?? null
        ).causedBy(action));
        if (!(e instanceof NodeApiError)) {
            dispatch(errorThrown(e));
        }
    }
}

async function commentDraftDeleteSaga(action: WithContext<CommentDraftDeleteAction>): Promise<void> {
    const {draft} = action.payload;

    if (draft?.id == null) {
        return;
    }

    await deleteDraft(action, draft.id);
    if (draft.receiverPostingId != null) {
        dispatch(commentDraftDeleted(draft.receiverName, draft.receiverPostingId).causedBy(action));
    }
}

async function commentComposeCancelSaga(action: WithContext<CommentComposeCancelAction>): Promise<void> {
    const draftId = select(state => state.detailedPosting.compose.draft?.id);

    if (draftId != null) {
        await deleteDraft(action, draftId);
    }
    dispatch(commentComposeCancelled().causedBy(action));
}

async function commentDeleteSaga(action: WithContext<CommentDeleteAction>): Promise<void> {
    const {commentId} = action.payload;

    const {postingId, receiverName, receiverPostingId} = select(state => ({
        postingId: getDetailedPostingId(state),
        receiverName: getCommentsState(state).receiverName,
        receiverPostingId: getCommentsState(state).receiverPostingId
    }));
    if (receiverName == null || receiverPostingId == null || postingId == null) {
        return;
    }
    dispatch(postingCommentCountUpdate(receiverPostingId, receiverName, -1).causedBy(action));
    try {
        const info = await Node.deleteComment(action, receiverName, receiverPostingId, commentId);
        dispatch(commentDeleted(receiverName, receiverPostingId, commentId).causedBy(action));
        dispatch(postingCommentsSet(postingId, info.total, REL_CURRENT).causedBy(action));
        await Node.deleteRemoteComment(action, REL_HOME, receiverName, receiverPostingId, commentId);
    } catch (e) {
        dispatch(commentDeleteFailed(receiverName, receiverPostingId, commentId).causedBy(action));
        dispatch(errorThrown(e));
    }
}

async function commentSetVisibilitySaga(action: WithContext<CommentSetVisibilityAction>): Promise<void> {
    const {commentId, premoderating, visible} = action.payload;

    const {
        receiverName, receiverPostingId, isOwner, ownerVisible, isSenior, seniorVisible
    } = select(state => ({
        receiverName: getCommentsState(state).receiverName,
        receiverPostingId: getCommentsState(state).receiverPostingId,
        isOwner: isPermitted("edit", getComment(state, commentId), "owner", state),
        ownerVisible: isPrincipalIn("view", getComment(state, commentId), "public", "public", {
            useOperations: "owner"
        }),
        isSenior: isPermitted("overrideComment", getDetailedPosting(state), "owner", state),
        seniorVisible: isPrincipalIn("view", getComment(state, commentId), "unset", ["unset", "public"], {
            useOperations: "senior"
        })
    }));
    if (receiverName == null || receiverPostingId == null) {
        return;
    }

    const commentText: CommentText = {};
    if (isSenior && seniorVisible !== visible && !(isOwner && !visible)) {
        commentText.seniorOperations = {
            view: visible ? "unset" : "private"
        }
        if (premoderating && visible) {
            commentText.premoderating = false;
        }
    }
    if (isOwner && ownerVisible !== visible) {
        commentText.operations = {
            view: visible ? "public" : "private"
        }
    }

    try {
        const comment = await Node.updateComment(action, receiverName, receiverPostingId, commentId, commentText);
        dispatch(commentSet(receiverName, comment).causedBy(action));
    } catch (e) {
        dispatch(errorThrown(e));
    }
}

async function focusedCommentLoadSaga(action: WithContext<FocusedCommentLoadAction>): Promise<void> {
    const {receiverName, receiverPostingId, focusedCommentId} = select(getCommentsState);
    if (receiverName == null || receiverPostingId == null || focusedCommentId == null) {
        return;
    }
    try {
        const comment = await Node.getComment(action, receiverName, receiverPostingId, focusedCommentId, false,
            ["comment.not-found"]);
        dispatch(focusedCommentLoaded(receiverName, comment).causedBy(action));
        loadRemoteMediaInEntry(action, comment);
    } catch (e) {
        dispatch(focusedCommentLoadFailed(receiverName, receiverPostingId).causedBy(action));
        if (!(e instanceof NodeApiError) || e.errorCode !== "comment.not-found") {
            dispatch(errorThrown(e));
        } else {
            dispatch(flashBox(i18n.t("comment-not-found")).causedBy(action));
        }
    }
}

async function commentCopyLinkSaga(action: WithContext<CommentCopyLinkAction>): Promise<void> {
    const {id, postingId} = action.payload;
    try {
        const href = await postingGetLink(action, postingId, REL_CURRENT);
        await clipboardCopy(href + ut`?comment=${id}`);
        if (!Browser.isAndroidBrowser()) {
            dispatch(flashBox(i18n.t("link-copied")).causedBy(action));
        }
    } catch (e) {
        dispatch(errorThrown(e));
    }
}

async function commentDialogCommentLoadSaga(action: WithContext<CommentDialogCommentLoadAction>): Promise<void> {
    const {receiverName, receiverPostingId, commentId} = select(state => ({
        receiverName: getCommentsState(state).receiverName,
        receiverPostingId: getCommentsState(state).receiverPostingId,
        commentId: getCommentDialogCommentId(state)
    }));
    if (receiverName == null || receiverPostingId == null || commentId == null) {
        return;
    }
    try {
        const comment = await Node.getComment(
            action, receiverName, receiverPostingId, commentId, true, ["comment.not-found"]
        );
        dispatch(commentDialogCommentLoaded(comment).causedBy(action));
        loadRemoteMediaInEntry(action, comment);
    } catch (e) {
        dispatch(commentDialogCommentLoadFailed().causedBy(action));
        dispatch(errorThrown(e));
    }
}

async function commentDialogCommentResetSaga(action: WithContext<CommentDialogCommentResetAction>): Promise<void> {
    const {draftId, closeDialog} = action.payload;

    if (closeDialog) {
        dispatch(closeCommentDialog().causedBy(action));
    }
    if (draftId != null) {
        await deleteDraft(action, draftId);
    }
}

async function commentVerifySaga(action: WithContext<CommentVerifyAction>): Promise<void> {
    const {receiverName, receiverPostingId} = select(getCommentsState);
    if (receiverName == null || receiverPostingId == null) {
        return;
    }
    try {
        await Node.verifyRemoteComment(action, REL_HOME, receiverName, receiverPostingId, action.payload.commentId);
    } catch (e) {
        dispatch(commentVerifyFailed(receiverName, receiverPostingId, action.payload.commentId).causedBy(action));
        dispatch(errorThrown(e));
    }
}

async function commentReactSaga(action: WithContext<CommentReactAction>): Promise<void> {
    await homeIntroduced();
    const {id, negative, emoji} = action.payload;
    const {homeOwnerName, homeOwnerFullName, homeOwnerSourceUri, homeOwnerGender, homeOwnerAvatar} = action.context;

    const {seniorName, comments: {receiverName, receiverPostingId}, seniorReaction} = select(state => ({
        seniorName: getDetailedPosting(state)?.ownerName,
        comments: getCommentsState(state),
        seniorReaction: getComment(state, id)?.seniorReaction
    }));
    if (receiverName == null || receiverPostingId == null || seniorName == null) {
        return;
    }
    try {
        const created = await Node.createCommentReaction(
            action, receiverName, receiverPostingId, id, {
                ownerName: homeOwnerName, ownerFullName: homeOwnerFullName, ownerSourceUri: homeOwnerSourceUri,
                ownerGender: homeOwnerGender,
                ownerAvatar: toAvatarDescription(homeOwnerAvatar), negative, emoji
            }
        );
        const seniorAttributes = seniorName !== homeOwnerName
            ? extractAttributes(seniorReaction)
            : {negative, emoji};
        dispatch(commentReactionSet(
            receiverName, id, receiverPostingId, {negative, emoji}, seniorAttributes, created.totals
        ).causedBy(action));
        await Node.createRemoteCommentReaction(
            action, REL_HOME, receiverName, receiverPostingId, id, {negative, emoji}
        );
    } catch (e) {
        dispatch(errorThrown(e));
    }
}

async function commentReactionLoadSaga(action: WithContext<CommentReactionLoadAction>): Promise<void> {
    await homeIntroduced();
    const {id} = action.payload;
    const {homeOwnerName} = action.context;

    const {seniorName, comments: {receiverName, receiverPostingId}} = select(state => ({
        seniorName: getDetailedPosting(state)?.ownerName,
        comments: getCommentsState(state)
    }));
    if (receiverName == null || receiverPostingId == null || seniorName == null || homeOwnerName == null) {
        return;
    }
    try {
        const reaction = extractAttributes(
            await Node.getCommentReaction(action, receiverName, receiverPostingId, id, homeOwnerName));
        const seniorReaction = extractAttributes(
            await Node.getCommentReaction(action, receiverName, receiverPostingId, id, seniorName));
        const totals = await Node.getCommentReactionTotals(action, receiverName, receiverPostingId, id);
        dispatch(commentReactionSet(
            receiverName, id, receiverPostingId, reaction, seniorReaction, totals
        ).causedBy(action));
    } catch (e) {
        if (Browser.isDevMode()) {
            dispatch(errorThrown(e));
        }
    }
}

async function commentReactionDeleteSaga(action: WithContext<CommentReactionDeleteAction>): Promise<void> {
    await homeIntroduced();
    const {id} = action.payload;
    const {homeOwnerName} = action.context;

    const {seniorName, comments: {receiverName, receiverPostingId}, seniorReaction} = select(state => ({
        seniorName: getDetailedPosting(state)?.ownerName,
        comments: getCommentsState(state),
        seniorReaction: getComment(state, id)?.seniorReaction
    }));
    if (receiverName == null || receiverPostingId == null || homeOwnerName == null) {
        return;
    }
    try {
        const totals = await Node.deleteCommentReaction(action, receiverName, receiverPostingId, id,
            homeOwnerName);
        const seniorAttributes = seniorName !== homeOwnerName
            ? extractAttributes(seniorReaction)
            : null;
        dispatch(commentReactionSet(
            receiverName, id, receiverPostingId, null, seniorAttributes, totals
        ).causedBy(action));
        await Node.deleteRemoteCommentReaction(action, REL_HOME, receiverName, receiverPostingId, id);
    } catch (e) {
        dispatch(errorThrown(e));
    }
}

function extractAttributes(reactionInfo: ReactionInfo | null | undefined): ReactionAttributes | null {
    if (reactionInfo == null) {
        return null;
    }
    const {negative, emoji} = reactionInfo;
    return negative != null && emoji != null ? {negative, emoji} : null;
}

function commentReplySaga(action: CommentReplyAction): void {
    const {commentId, ownerName, ownerFullName, ownerSourceUri, heading} = action.payload;

    const {replied, repliedToName} = select(state => ({
        replied: isCommentComposerReplied(state),
        repliedToName: getCommentComposerRepliedToName(state)
    }));

    const html = getWindowSelectionHtml();

    if (!replied) {
        dispatch(commentRepliedToSet(commentId, ownerName, ownerFullName, ownerSourceUri, heading).causedBy(action));
        document.dispatchEvent(uiEventCommentQuote(html || undefined));
    } else {
        if (html) {
            if (ownerName !== repliedToName) {
                document.dispatchEvent(uiEventCommentQuote(html, ownerName, ownerFullName));
            } else {
                document.dispatchEvent(uiEventCommentQuote(html));
            }
        } else {
            document.dispatchEvent(uiEventCommentQuote(undefined, ownerName, ownerFullName));
        }
    }
    dispatch(commentsScrollToComposer().causedBy(action));
}

async function glanceCommentLoadSaga(action: WithContext<GlanceCommentLoadAction>): Promise<void> {
    const {receiverName, receiverPostingId, commentId, comment} = select(
        state => {
            const comments = getCommentsState(state);
            return {
                receiverName: comments.receiverName,
                receiverPostingId: comments.receiverPostingId,
                commentId: comments.glanceCommentId,
                comment: comments.glanceCommentId != null ? getComment(state, comments.glanceCommentId) : null
            }
        }
    );

    if (receiverName == null || receiverPostingId == null || commentId == null) {
        return;
    }
    if (comment != null) {
        dispatch(glanceCommentLoaded(receiverName, comment).causedBy(action));
        return;
    }

    try {
        const comment = await Node.getComment(
            action, receiverName, receiverPostingId, commentId, false, ["comment.not-found"]
        );
        dispatch(glanceCommentLoaded(receiverName, comment).causedBy(action));
        loadRemoteMediaInEntry(action, comment);
    } catch (e) {
        dispatch(glanceCommentLoadFailed(receiverName, receiverPostingId).causedBy(action));
        dispatch(errorThrown(e));
    }
}

async function attachmentCopyLinkSaga(action: WithContext<AttachmentCopyLinkAction>): Promise<void> {
    let {nodeName, media} = action.payload;

    nodeName = absoluteNodeName(nodeName, action.context);
    try {
        const nodeUri = await getNodeUri(action, nodeName);
        await clipboardCopy(
            universalLocation(null, nodeName, nodeUri, urlWithParameters("/media/" + media.path, {download: true}))
        );
        if (!Browser.isAndroidBrowser()) {
            dispatch(flashBox(i18n.t("link-copied")).causedBy(action));
        }
    } catch (e) {
        dispatch(errorThrown(e));
    }
}
